import { Component, Input, OnInit } from '@angular/core';
import { OperationResult } from '../../../models/server-result/operation-result';
import { HttpErrorResponse } from '@angular/common/http';
import { ReportingApiService } from '../../../server-api/reporting-api/reporting-api.service';
import { ServerApiService } from '../../../server-api/server-api.service';
import { ToasterService } from '../../../shared-components/custom-toaster/toaster.service';
import { FactoringContractsSearchCriteria } from '../../../models/contracts/factoring/factoring-contracts-search-criteria';
import { FactoringConnectionsSearchCriteria } from '../../../models/contracts/factoring/factoring-connections-search-criteria';
import { FactoringConnection } from '../../../models/contracts/factoring/factoring-connection';
import { CurrencyCode } from '../../../models/enums/currency-code';
import { Router } from '@angular/router';
import { FileIntegrationStateService } from '../../../services/file-integration-state.service';
import { SearchFilesIntegrationComponent } from '../../files-integration/search-files-integration/search-files-integration.component';
import { FileStatus } from '../../../models/enums/FileIntegration/integration-file-status';
import { TranslateService } from '../../../services/translate/translate.service';
import { DashboardHelperService } from '../dashboard-helper.service';

@Component({
	selector: 'app-dashboard-factoring-tab',
	templateUrl: './dashboard-factoring-tab.component.html',
	styleUrls: ['../private-home.component.scss'],
	standalone: false,
})
export class DashboardFactoringTabComponent implements OnInit {
	@Input() public currentMonth: Date;
	@Input() public canNavigate: boolean = true;

	public factoringApprovedCreditAssignments: number = 0;
	public factoringCurrentCreditAssignmentsAmount: number = 0;
	public factoringApprovedAdvances: number = 0;
	public factoringContractedAmount: number = 0;
	public factoringContractDocumentPortfolioAmount: number = 0;
	public factoringContractAdvanceableAmount: number = 0;
	public factoringSuppliersWithActiveContractsCompaniesCount: number = 0;
	public factoringBuyersWithActiveConnectionsCompaniesCount: number = 0;
	public factoringBuyersActiveConnections: number = 0;
	public factoringPendingApprovalDocumentsAmount: number = 0;

	protected CurrencyCode = CurrencyCode;
	protected FileStatus = FileStatus;

	public loadingFactoringApprovedCreditAssignments: boolean = false;
	public loadingFactoringCurrentCreditAssignmentsAmount: boolean = false;
	public loadingFactoringApprovedAdvances: boolean = false;
	public loadingFactoringContractedAmount: boolean = false;
	public loadingFactoringContractDocumentPortfolioAmount: boolean = false;
	public loadingFactoringContractAdvanceableAmount: boolean = false;
	public loadingFactoringSuppliersWithActiveContractsCompaniesCount: boolean = false;
	public loadingFactoringBuyersWithActiveConnectionsCompaniesCount: boolean = false;
	public loadingFactoringBuyersActiveConnections: boolean = false;
	public loadingFactoringPendingApprovalDocumentsAmount: boolean = false;

	constructor(
		private reportingApiService: ReportingApiService,
		private serverApiService: ServerApiService,
		private toasterService: ToasterService,
		private router: Router,
		private fileIntegrationStateService: FileIntegrationStateService,
		private translateService: TranslateService,
		private dashboardHelper: DashboardHelperService
	) {}

	ngOnInit() {
		this.refresh();
	}

	public refresh(): void {
		this.getFactoringApprovedCreditAssignments(CurrencyCode.Euro);
		this.getFactoringCurrentCreditAssignmentsAmount(CurrencyCode.Euro);
		this.getFactoringApprovedAdvances(CurrencyCode.Euro);
		this.getFactoringSuppliersWithActiveContractsCompaniesCount();
		this.getFactoringBuyersWithActiveConnectionsCompaniesCount();
		this.getFactoringBuyersActiveConnections();
		this.getPendingApprovalDocumentsAmount(CurrencyCode.Euro);
	}

	private getFactoringApprovedCreditAssignments(currency: string): void {
		const creditAssignmentsDocumentStates = ['Approved'];

		this.dashboardHelper.loadMetric(
			this.reportingApiService.getFactoringApprovedCreditAssignments(creditAssignmentsDocumentStates, currency),
			(loading) => (this.loadingFactoringApprovedCreditAssignments = loading),
			(value) => (this.factoringApprovedCreditAssignments = value),
			'[DashboardFactoringTabComponent] Invalid result from getFactoringApprovedCreditAssignments',
			'[DashboardFactoringTabComponent] HttpError result from getFactoringApprovedCreditAssignments'
		);
	}

	private getFactoringCurrentCreditAssignmentsAmount(currency: string): void {
		this.dashboardHelper.loadMetric(
			this.reportingApiService.getFactoringCurrentCreditAssignmentsAmount(currency),
			(loading) => (this.loadingFactoringCurrentCreditAssignmentsAmount = loading),
			(value) => (this.factoringCurrentCreditAssignmentsAmount = value),
			'[DashboardFactoringTabComponent] Invalid result from getFactoringCurrentCreditAssignmentsAmount()'
		);
	}

	private getFactoringApprovedAdvances(currency: string): void {
		const advancesStates = ['Approved'];

		this.dashboardHelper.loadMetric(
			this.reportingApiService.getFactoringApprovedAdvances(advancesStates, currency),
			(loading) => (this.loadingFactoringApprovedAdvances = loading),
			(value) => (this.factoringApprovedAdvances = value),
			'[DashboardFactoringTabComponent] Invalid result from getFactoringApprovedAdvances',
			'[DashboardFactoringTabComponent] HttpError result from getFactoringApprovedAdvances'
		);
	}

	private getPendingApprovalDocumentsAmount(currency: string) {
		this.factoringPendingApprovalDocumentsAmount = 0;
		this.dashboardHelper.loadMetric(
			this.reportingApiService.getPendingApprovalDocumentsAmount(currency),
			(loading) => (this.loadingFactoringPendingApprovalDocumentsAmount = loading),
			(value) => (this.factoringPendingApprovalDocumentsAmount = value.Factoring),
			'[DashboardFactoringTabComponent] Invalid result from getPendingApprovalDocumentsAmount',
			'[DashboardFactoringTabComponent] HttpError result from getPendingApprovalDocumentsAmount'
		);
	}

	private getFactoringSuppliersWithActiveContractsCompaniesCount(): void {
		this.loadingFactoringContractedAmount = true;
		this.loadingFactoringContractAdvanceableAmount = true;
		this.loadingFactoringContractDocumentPortfolioAmount = true;
		this.loadingFactoringSuppliersWithActiveContractsCompaniesCount = true;

		this.factoringContractedAmount = 0;
		this.factoringContractAdvanceableAmount = 0;
		this.factoringContractDocumentPortfolioAmount = 0;
		this.factoringSuppliersWithActiveContractsCompaniesCount = 0;

		const criteria: FactoringContractsSearchCriteria = new FactoringContractsSearchCriteria();
		criteria.PageNumber = 1;
		criteria.OrderDescending = true;
		criteria.RowsPerPage = 2147483647;
		criteria.RestrictionCriteria = new FactoringContractsSearchCriteria();
		criteria.RestrictionCriteria.ContractStateList = ['Active'];

		this.serverApiService.searchFactoringContracts(criteria).subscribe((getFactoringContracts) => {
			this.loadingFactoringContractedAmount = false;
			this.loadingFactoringContractAdvanceableAmount = false;
			this.loadingFactoringContractDocumentPortfolioAmount = false;
			this.loadingFactoringSuppliersWithActiveContractsCompaniesCount = false;
			if (!getFactoringContracts.IsValid) {
				if (getFactoringContracts.Errors && getFactoringContracts.Errors.length > 0) {
					this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
						errors: getFactoringContracts.Errors,
						autoClose: false,
					});
				}
			} else {
				if (!!getFactoringContracts.Data && getFactoringContracts.Data.length > 0) {
					getFactoringContracts.Data.forEach((contract) => {
						this.factoringContractedAmount += contract.ContractAmount;
						this.factoringContractAdvanceableAmount += contract.AdvanceableAmount;
						this.factoringContractDocumentPortfolioAmount += contract.DocumentPortfolioAmount;
					});

					this.factoringSuppliersWithActiveContractsCompaniesCount = this.dashboardHelper.distinctCount(
						getFactoringContracts.Data.map((c) => c.Supplier.Id)
					);
				}
			}
		});
	}

	private getFactoringBuyersWithActiveConnectionsCompaniesCount() {
		this.loadingFactoringBuyersWithActiveConnectionsCompaniesCount = true;
		this.factoringBuyersWithActiveConnectionsCompaniesCount = 0;
		const criteria: FactoringConnectionsSearchCriteria = new FactoringConnectionsSearchCriteria();
		criteria.PageNumber = 1;
		criteria.RowsPerPage = 2147483647;
		criteria.RestrictionCriteria = new FactoringConnectionsSearchCriteria();
		criteria.RestrictionCriteria.ConnectionStateList = ['Active'];
		criteria.RestrictionCriteria.ContractStateList = ['Active'];

		this.serverApiService.searchFactoringConnections(criteria).subscribe({
			next: (operationResult: OperationResult<FactoringConnection[]>) => {
				this.loadingFactoringBuyersWithActiveConnectionsCompaniesCount = false;
				if (operationResult.IsValid) {
					if (!!operationResult.Data && operationResult.Data.length > 0) {
						this.factoringBuyersWithActiveConnectionsCompaniesCount = this.dashboardHelper.distinctCount(
							operationResult.Data.map((c) => c.Buyer.Id)
						);
					}
				} else {
					this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
						errors: operationResult.Errors,
						autoClose: false,
					});
				}
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.loadingFactoringBuyersWithActiveConnectionsCompaniesCount = false;
				console.error('error: ' + httpErrorResponse.message);
				this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
			},
		});
	}

	private getFactoringBuyersActiveConnections() {
		this.factoringBuyersActiveConnections = 0;
		this.dashboardHelper.loadMetric<number>(
			this.reportingApiService.getFactoringBuyersActiveConnections(),
			(loading) => (this.loadingFactoringBuyersActiveConnections = loading),
			(value) => (this.factoringBuyersActiveConnections = value),
			'[DashboardFactoringTabComponent] Invalid result from getFactoringBuyersActiveConnections()'
		);
	}

	public navigateToFilesIntegration(key: string): void {
		if (this.canNavigate) {
			SearchFilesIntegrationComponent.navigateWithFileStatus(this.router, this.fileIntegrationStateService, key);
		}
	}
}
